import { useState, useRef, useEffect } from 'react';
import { ArrowRight, Globe, Mail, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PriceInquiryModal } from '../components/ui';
import { pricingTranslations } from '../locales/pricing';
import { Helmet } from 'react-helmet-async';

const PricingPage = () => {
    const { lang, setLang } = useLanguage();
    const t = pricingTranslations[lang as keyof typeof pricingTranslations];
    const navigate = useNavigate();
    const [showLangMenu, setShowLangMenu] = useState(false);
    const [showPriceInquiryModal, setShowPriceInquiryModal] = useState(false);
    const langMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
                setShowLangMenu(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const supportedLanguages = [
        { code: 'en', label: 'EN', flag: '🇬🇧' },
        { code: 'tr', label: 'TR', flag: '🇹🇷' },
        { code: 'es', label: 'ES', flag: '🇪🇸' },
        { code: 'de', label: 'DE', flag: '🇩🇪' },
        { code: 'zh', label: 'ZH', flag: '🇨🇳' },
        { code: 'ja', label: 'JA', flag: '🇯🇵' },
        { code: 'ko', label: 'KO', flag: '🇰🇷' }
    ];

    return (
        <div className="min-h-screen bg-slate-50 text-slate-800 selection:bg-orange-100">
            <Helmet>
                <title>{t.title.replace('<br />', ' ')} | Lashing Calculator Engine</title>
                <meta name="description" content={t.subtitle} />
            </Helmet>
            {/* Background Effects */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-10%] right-[-5%] w-[45%] h-[45%] bg-orange-100/40 rounded-full blur-[100px]"></div>
                <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] bg-blue-50/50 rounded-full blur-[100px]"></div>
            </div>

            {/* Navigation */}
            <nav className="relative z-50 py-3 px-4 sm:px-6 lg:px-8 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => {
                            navigate('/');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                    >
                        <div className="flex items-center gap-3">
                            <img src="/sonlogo.png" alt="Lashing Calculator Shield" className="w-32 h-32 object-contain" />
                        </div>
                    </div>

                    <div className="flex items-center gap-6">
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center gap-2 text-slate-600 hover:text-orange-600 transition-colors text-sm font-bold"
                        >
                            <ArrowRight className="w-4 h-4 rotate-180" />
                            {t.nav.back}
                        </button>

                        <div className="relative" ref={langMenuRef}>
                            <button
                                onClick={() => setShowLangMenu(!showLangMenu)}
                                className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-slate-50 text-slate-900 rounded-lg transition-all text-sm font-bold border border-slate-200 shadow-sm"
                            >
                                <span className="text-xl leading-none">{supportedLanguages.find(l => l.code === lang)?.flag || '🇬🇧'}</span>
                                <span className="text-slate-900 font-bold">{supportedLanguages.find(l => l.code === lang)?.label || 'EN'}</span>
                                <ArrowRight className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${showLangMenu ? 'rotate-90' : '-rotate-90'}`} />
                            </button>

                            <AnimatePresence>
                                {showLangMenu && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                        transition={{ duration: 0.15, ease: "easeOut" }}
                                        className="absolute right-0 mt-2 w-32 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden py-1 z-[100]"
                                    >
                                        {supportedLanguages.map((language) => (
                                            <button
                                                key={language.code}
                                                onClick={() => {
                                                    setLang(language.code as any);
                                                    setShowLangMenu(false);
                                                }}
                                                className={`w-full text-left flex items-center gap-2 px-4 py-2 text-sm font-bold transition-colors ${lang === language.code
                                                    ? 'bg-orange-50 text-orange-600'
                                                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                                    }`}
                                            >
                                                <span>{language.flag}</span>
                                                <span>{language.label}</span>
                                            </button>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        <button
                            onClick={() => navigate('/login')}
                            className="px-6 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold text-sm transition-all shadow-sm"
                        >
                            {t.nav.signIn}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative z-10 pt-16 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-4xl sm:text-6xl font-black mb-6 tracking-tight text-slate-900 leading-[1.1]">
                            {t.title.split(' ').slice(0, -1).join(' ')}
                            <br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500">
                                {t.title.split(' ').slice(-1)}
                            </span>
                        </h1>
                        <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-12 font-medium leading-relaxed">{t.subtitle}</p>
                    </motion.div>
                </div>
            </section>

            {/* Pricing Cards */}
            <section className="relative z-10 pb-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-3 gap-8 items-start">
                        {/* Basic Tier */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="p-8 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-2xl hover:border-slate-200 transition-all group"
                        >
                            <h3 className="text-xl font-bold text-slate-900 mb-2">{t.tiers.basic.name}</h3>
                            <p className="text-slate-500 text-sm mb-8 font-medium leading-relaxed">{t.tiers.basic.description}</p>
                            <div className="mb-8 font-black text-slate-900">
                                <div className="text-3xl font-black text-slate-900 mb-1">
                                    {t.tiers.basic.priceLabel || t.contactForPricing}
                                </div>
                                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">{t.tiers.basic.priceSubtext}</p>
                            </div>
                            <button
                                onClick={() => setShowPriceInquiryModal(true)}
                                className="w-full py-4 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold transition-all mb-8 shadow-lg flex items-center justify-center gap-2 group active:scale-95"
                            >
                                {t.tiers.basic.cta}
                                <Mail className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <ul className="space-y-4">
                                {t.tiers.basic.features.map((feature, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm text-slate-700 font-medium">
                                        <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Premium Tier */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="p-8 rounded-2xl bg-white border-[3px] border-orange-500 relative shadow-[0_32px_64px_-16px_rgba(234,88,12,0.15)] z-20"
                        >
                            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-orange-600 text-white px-6 py-1.5 rounded-full text-xs font-black uppercase tracking-[0.2em] shadow-lg">
                                {t.tiers.premium.badge}
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-2">{t.tiers.premium.name}</h3>
                            <p className="text-slate-500 text-sm mb-8 font-medium leading-relaxed">{t.tiers.premium.description}</p>
                            <div className="mb-8 font-black text-slate-900">
                                <div className="text-3xl font-black text-orange-600 mb-1">
                                    {t.tiers.premium.priceLabel || t.contactForPricing}
                                </div>
                                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">{t.tiers.premium.priceSubtext}</p>
                            </div>
                            <button
                                onClick={() => setShowPriceInquiryModal(true)}
                                className="w-full py-4 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-bold transition-all mb-8 shadow-xl shadow-orange-600/30 active:scale-95 flex items-center justify-center gap-2 group"
                            >
                                {t.tiers.premium.cta}
                                <Mail className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <ul className="space-y-4">
                                {t.tiers.premium.features.map((feature, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm text-slate-900 font-bold">
                                        <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Enterprise Tier */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="p-8 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-2xl hover:border-slate-200 transition-all"
                        >
                            <h3 className="text-xl font-bold text-slate-900 mb-2">{t.tiers.enterprise.name}</h3>
                            <p className="text-slate-500 text-sm mb-8 font-medium leading-relaxed">{t.tiers.enterprise.description}</p>
                            <div className="mb-8">
                                <div className="text-4xl font-black text-slate-900 mb-1">
                                    {t.tiers.enterprise.priceLabel}
                                </div>
                                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">{t.tiers.enterprise.priceSubtext}</p>
                            </div>
                            <div className="mt-8">
                                <button
                                    onClick={() => setShowPriceInquiryModal(true)}
                                    className="w-full py-4 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2 group active:scale-95"
                                >
                                    {t.tiers.enterprise.cta}
                                    <Mail className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                            <ul className="space-y-4 mt-8">
                                {t.tiers.enterprise.features.map((feature, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm text-slate-700 font-medium">
                                        <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-3xl font-black text-center text-slate-900 mb-16 underline decoration-orange-500 decoration-4 underline-offset-8">{t.faq.title}</h2>
                    <div className="space-y-6">
                        {t.faq.items.map((item, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.4, delay: i * 0.1 }}
                                className="p-8 rounded-xl bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-xl transition-all duration-300"
                            >
                                <h3 className="text-lg font-bold text-slate-900 mb-3">{item.question}</h3>
                                <p className="text-slate-600 font-medium leading-relaxed">{item.answer}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <PriceInquiryModal
                isOpen={showPriceInquiryModal}
                onClose={() => setShowPriceInquiryModal(false)}
            />
        </div>
    );
};

export default PricingPage;
